import React, { useEffect, useMemo, useState, useRef } from "react";
import styles from "./ReferenceSelector.module.scss";
import { useQuery, useLazyQuery } from "@apollo/client";
import gql from "graphql-tag";
import { getPlansByReferenceModel, indexToLetter, toPascalCase } from "../../utils/misc";
import _ from "lodash";
import useDidUpdateEffect from "../../hooks/useDidUpdateEffect";

import { TextField, Typography, MenuItem, useTheme } from "@material-ui/core";
import { Autocomplete, ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import Icon from "@mdi/react";
import {
  mdiLinkVariantOff,
  mdiRhombus,
  mdiRhombusSplit,
  mdiPoll,
  mdiBullseyeArrow,
  mdiAlertDecagram,
  mdiCheckboxMarked,
  mdiChartTimelineVariant,
  mdiFolderStar,
  mdiGoogleClassroom,
  mdiEye,
  mdiSelectGroup,
} from "@mdi/js";

import PlanPill from "../PlanPill/PlanPill";
import { ORG_NOTE_CONTEXTS } from "../../utils/const";

const PLAN_CATEGORIES = {
  "1 year": "1Y",
  "3 year": "3Y",
};

const ReferenceSelector = ({
  formik,
  disabled = false,
  refTypeName,
  refIdName,
  refIdLabel = (refType) => `Tied in ${_.startCase(refType)}`,
  refIdsName,
  refIdsLabel = (refType) => `Tied in ${_.startCase(refType)}s`,
  refContextName,
  refContextLabel = (refType) => `${_.startCase(refType)} Context`,
  useRefIds = false,
  useRefContext = false,
  handleChangeRefId,
  orgId,
  triggerRefetch,
  deptFilterSpid,
  currCorpPlanId,
  refTypeCasing = "pascal",
  dialogType = "todo",
}) => {
  const theme = useTheme();
  const { values, touched, errors, setFieldValue, getFieldProps } = formik;

  // const [valueKey, setValueKey] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [inputSearchTerm, setInputSearchTerm] = useState("");
  const debouncedSetSearchTermRef = useRef(_.debounce((newValue) => setSearchTerm(newValue), 700));

  const refType = _.camelCase(_.get(values, refTypeName, ""));
  const refId = _.get(values, refIdName);

  const isTodoDialog = dialogType === "todo";
  const isNoteDialog = dialogType === "note";

  const isTodoReference = ["todo", "issue"].includes(refType);
  const isPlanReference = refType === "plan";

  const [getReferencedTodo, { data: referencedTodoData, loading: referencedTodoLoading, refetch: referencedTodoRefetch }] =
    useLazyQuery(GET_REFERENCED_TODO_ISSUE);

  const {
    data,
    refetch,
    loading: queryLoading,
  } = useQuery(GET_NON_TODO_DATA, {
    variables: {
      organization: orgId,
      sharedPlanId: deptFilterSpid,
      searchTerm,
      corpPlan: currCorpPlanId || null,
      currQuarter: isTodoDialog,
    },
  });

  const {
    data: todosData,
    refetch: todosRefetch,
    loading: todosLoading,
    fetchMore: fetchMoreTodos,
  } = useQuery(GET_MORE_TODOS, {
    variables: {
      organization: orgId,
      sharedPlanId: deptFilterSpid,
      limit: 25,
      cursor: null,
      searchTerm,
      oneYearCorpPlan: currCorpPlanId || null,
    },
  });

  const {
    data: issuesData,
    refetch: issuesRefetch,
    loading: issuesLoading,
    fetchMore: fetchMoreIssues,
  } = useQuery(GET_MORE_ISSUES, {
    variables: {
      organization: orgId,
      sharedPlanId: deptFilterSpid,
      limit: 25,
      cursor: null,
      searchTerm,
      oneYearCorpPlan: currCorpPlanId || null,
    },
  });

  const menuItems = useMemo(() => {
    if (_.isEmpty(data)) return [];

    let menuItemsArr = [],
      fieldArr = [],
      orderArr = [];
    switch (refType) {
      case "plan":
        const threeYearPlans = _.get(data, "threeYearPlans", []);
        const oneYearPlans = _.get(data, "oneYearPlans", []);
        menuItemsArr = [...threeYearPlans, ...oneYearPlans];
        break;
      case "project":
        menuItemsArr = _.get(data, "projects", []);
        fieldArr = ["plan.departmentName", "value"];
        orderArr = ["asc", "asc"];
        break;
      case "objective":
        menuItemsArr = _.get(data, "objectives", []);
        fieldArr = ["category", "plan.departmentName", "number"];
        orderArr = ["desc", "asc", "asc"];
        break;
      case "rock":
        menuItemsArr = _.get(data, "rocks", []);
        fieldArr = ["plan.departmentName", "objective.number", "orgNumber"];
        orderArr = ["asc", "asc", "desc"];
        break;
      case "successCriteria":
        menuItemsArr = _.get(data, "successCriterias", []);
        fieldArr = ["rock.plan.departmentName", "rock.orgNumber", "orderIdx"];
        orderArr = ["asc", "desc", "asc"];
        break;
      case "metric":
        const metrics = _.get(data, "metrics", []);
        menuItemsArr = _.uniqBy(metrics, "value");
        fieldArr = ["category", "plan.departmentName", "number"];
        orderArr = ["desc", "asc", "asc"];
        break;
      case "weeklyTarget":
        menuItemsArr = _.get(data, "weeklyTargets", []);
        fieldArr = ["plans.0.departmentName", "value"];
        orderArr = ["asc", "asc"];
        break;
      case "issue":
        menuItemsArr = _.get(issuesData, "results.todos", []);
        fieldArr = ["orgNumber"];
        orderArr = ["desc"];
        break;
      case "todo":
        menuItemsArr = _.get(todosData, "results.todos", []);
        fieldArr = ["orgNumber"];
        orderArr = ["desc"];
        break;
      default:
    }

    const referencedTodo = _.get(referencedTodoData, "todo");
    if (isTodoReference && !menuItemsArr.some((item) => item.id === refId) && _.get(referencedTodo, "id") === refId) {
      // if reference is a todo or issue, add the reference if it has not been lazy loaded yet
      menuItemsArr = [...menuItemsArr, referencedTodo];
    }

    if (!isPlanReference) {
      menuItemsArr = _.orderBy(menuItemsArr, fieldArr, orderArr);
    }

    return menuItemsArr;
  }, [refType, refId, data, issuesData, todosData, referencedTodoData, isTodoReference]);

  const loadMoreItems = (event, refType) => {
    const userScrolledToTheBottom = Math.abs(event.target.scrollHeight - event.target.clientHeight - event.target.scrollTop) < 1;
    if (isTodoReference && userScrolledToTheBottom) {
      //user is at the end of the list so load more items
      if (refType === "todo" && !_.isNil(_.get(todosData, "results.nextCursor"))) {
        fetchMoreTodos({
          variables: {
            organization: orgId,
            sharedPlanId: deptFilterSpid,
            limit: 25,
            cursor: todosData.results.nextCursor,
            searchTerm,
          },
        });
      } else if (refType === "issue" && !_.isNil(_.get(issuesData, "results.nextCursor"))) {
        fetchMoreIssues({
          variables: {
            organization: orgId,
            sharedPlanId: deptFilterSpid,
            limit: 25,
            cursor: issuesData.results.nextCursor,
            searchTerm,
          },
        });
      }
    }
  };

  const caseVal = (value) => {
    if (_.isNil(value)) return value;

    let ret;
    switch (refTypeCasing) {
      case "pascal":
        ret = toPascalCase(value);
        break;
      case "camel":
        ret = _.camelCase(value);
        break;
      default:
    }
    return ret;
  };

  const renderReferenceItem = (item) => {
    const { category } = item;

    let identifier = _.startCase(refType),
      itemContent;
    switch (refType) {
      case "plan":
        identifier = _.get(PLAN_CATEGORIES, category, identifier);
        const deptName = _.get(item, "departmentName");

        itemContent = (
          <Typography noWrap className={styles.tieInItemText}>
            <strong>{identifier}:</strong> {deptName}
          </Typography>
        );
        break;
      case "objective":
        identifier = _.get({ "1 year": "1YO", "3 year": "3YO" }, category, identifier);
        const objValue = _.get(item, "value");
        const objNumber = _.get(item, "number");
        const objPlan = _.get(item, "plan", {});
        const objPlanTheme = _.get(objPlan, "theme", {});

        itemContent = (
          <div className={styles.tieInItemText}>
            <Typography noWrap>
              <strong>({identifier})</strong> {objNumber}. {objValue}
            </Typography>
            <Typography
              noWrap
              variant="caption"
              style={{
                fontStyle: "italic",
                color: theme.palette.text.secondary,
              }}
            >
              {objPlanTheme}
            </Typography>
          </div>
        );

        break;
      case "metric":
        identifier = _.get({ year: "3YM", quarter: "1YM" }, category, identifier);
        const mtrcValue = _.get(item, "value");
        const mtrcNumber = _.get(item, "number");
        const mtrcPlan = _.get(item, "plan", {});
        const mtrcPlanTheme = _.get(mtrcPlan, "theme", {});

        itemContent = (
          <div className={styles.tieInItemText}>
            <Typography noWrap>
              <strong>({identifier})</strong> {mtrcNumber}. {mtrcValue}
            </Typography>
            <Typography
              noWrap
              variant="caption"
              style={{
                fontStyle: "italic",
                color: theme.palette.text.secondary,
              }}
            >
              {mtrcPlanTheme}
            </Typography>
          </div>
        );
        break;
      case "rock":
        const rockValue = _.get(item, "value");
        const rockOrgNumber = _.get(item, "orgNumber");
        const rockObjective = _.get(item, "objective", {});
        const rockObjValue = _.get(rockObjective, "value");
        const rockObjNumber = _.get(rockObjective, "number");

        itemContent = (
          <div className={styles.tieInItemText}>
            <Typography noWrap>
              <strong>R{rockOrgNumber}:</strong> {rockValue}
            </Typography>
            <Typography
              noWrap
              variant="caption"
              style={{
                fontStyle: "italic",
                color: theme.palette.text.secondary,
              }}
            >
              {rockObjNumber}. {rockObjValue}
            </Typography>
          </div>
        );
        break;
      case "successCriteria":
        const scValue = _.get(item, "value");
        const scOrderIdx = _.get(item, "orderIdx");
        const scRock = _.get(item, "rock", {});
        const scRockValue = _.get(scRock, "value");
        const scRockOrgNumber = _.get(scRock, "orgNumber");

        const scLetterOrder = indexToLetter(scOrderIdx);

        itemContent = (
          <div className={styles.tieInItemText}>
            <Typography noWrap>
              <strong>
                R{scRockOrgNumber}
                {scLetterOrder}:
              </strong>{" "}
              {scValue}
            </Typography>
            <Typography
              noWrap
              variant="caption"
              style={{
                fontStyle: "italic",
                color: theme.palette.text.secondary,
              }}
            >
              R{scRockOrgNumber}: {scRockValue}
            </Typography>
          </div>
        );
        break;
      case "weeklyTarget":
        identifier = _.get({ "1 year": "1YO", "3 year": "3YO" }, category, identifier);
        const kpiValue = _.get(item, "value");

        itemContent = (
          <Typography noWrap className={styles.tieInItemText}>
            <strong>KPI:</strong> {kpiValue}
          </Typography>
        );
        break;
      case "todo":
      case "issue":
        const todoValue = _.get(item, "value");
        const todoOrgNumber = _.get(item, "orgNumber");
        const todoDescription = _.get(item, "description");

        itemContent = (
          <div className={styles.tieInItemText}>
            <Typography noWrap>
              <strong>
                {identifier.charAt(0)}
                {todoOrgNumber}:
              </strong>{" "}
              {todoValue}
            </Typography>
            <Typography
              noWrap
              variant="caption"
              style={{
                fontStyle: "italic",
                color: theme.palette.text.secondary,
              }}
            >
              {todoDescription}
            </Typography>
          </div>
        );
        break;
      default:
        itemContent = (
          <Typography noWrap className={styles.tieInItemText}>
            <strong>{identifier}:</strong> {_.get(item, "value")}
          </Typography>
        );
        break;
    }

    return itemContent;
  };

  const renderReferenceContext = (refType) => {
    const contextByRefType = { organization: ORG_NOTE_CONTEXTS };
    const context = _.get(contextByRefType, refType);

    return (
      <TextField
        select
        fullWidth
        label={refContextLabel(refType)}
        variant="outlined"
        {...getFieldProps(refContextName)}
        error={Boolean(_.get(touched, refContextName) && _.get(errors, refContextName))}
        helperText={_.get(touched, refContextName) && _.get(errors, refContextName)}
        disabled={disabled}
      >
        {Object.entries(context).map(([key, val]) => {
          return (
            <MenuItem key={key} value={key}>
              {val}
            </MenuItem>
          );
        })}
      </TextField>
    );
  };

  const renderMultiSelectRefs = (refType) => {
    const mapFuncByRefType = {
      plan: (el) => {
        const { id, departmentName, category } = el;
        const ctgry = _.get(PLAN_CATEGORIES, category);
        const deptStr = `${departmentName} (${ctgry})`;

        return (
          <MenuItem key={id} value={id}>
            {deptStr}
          </MenuItem>
        );
      },
    };
    const mapFunc = _.get(mapFuncByRefType, refType);

    return (
      <TextField
        select
        fullWidth
        label={refIdsLabel(refType)}
        variant="outlined"
        {...getFieldProps(refIdsName)}
        value={_.get(values, refIdsName) || []}
        SelectProps={{
          multiple: true,
        }}
        error={Boolean(_.get(touched, refIdsName) && _.get(errors, refIdsName))}
        helperText={_.get(touched, refIdsName) && _.get(errors, refIdsName)}
        disabled={disabled}
      >
        {menuItems.map(mapFunc)}
      </TextField>
    );
  };

  useEffect(() => {
    if (isTodoReference) {
      getReferencedTodo({
        variables: {
          id: refId,
        },
      });
    }
  }, [isTodoReference, refId]);

  useEffect(() => {
    if (debouncedSetSearchTermRef.current) {
      debouncedSetSearchTermRef.current.cancel();
      debouncedSetSearchTermRef.current(inputSearchTerm);
    }
  }, [inputSearchTerm]);

  useDidUpdateEffect(() => {
    refetch();
    todosRefetch();
    issuesRefetch();

    if (referencedTodoRefetch) {
      referencedTodoRefetch();
    }
  }, [triggerRefetch]);

  const menuItemsById = _.keyBy(menuItems, "id");
  const selectedItem = _.get(menuItemsById, refId, "");

  // console.log({
  //   refTypeName,
  //   refIdName,
  //   refContextName,
  //   refTypeTouched: _.get(touched, refTypeName),
  //   refTypeError: _.get(errors, refTypeName),
  //   refObjTouched: _.get(touched, refIdName),
  //   refObjError: _.get(errors, refIdName),
  //   refContextTouched: _.get(touched, refContextName),
  //   refContextError: _.get(errors, refContextName),
  // });

  return (
    <>
      <div className={styles.modelButtons}>
        <ToggleButtonGroup
          exclusive
          value={refType}
          onChange={(e, value) => {
            if (isTodoDialog || (isNoteDialog && value !== null)) {
              setFieldValue(refTypeName, caseVal(value)).then(() => {
                setFieldValue(refIdName, null);
                setFieldValue(refContextName, null);
              });
              setInputSearchTerm("");
            }
          }}
          className={styles.toggleButtonGroup}
        >
          {isNoteDialog && [
            <ToggleButton value="organization" className={styles.toggleButton} disabled={disabled}>
              <span className={styles.toggleButtonLabel}>Org.</span>
              <Icon path={mdiEye} size={1.25} />
            </ToggleButton>,
            <ToggleButton value="plan" className={styles.toggleButton} disabled={disabled}>
              <span className={styles.toggleButtonLabel}>Dept.</span>
              <Icon path={mdiSelectGroup} size={1.25} />
            </ToggleButton>,
            <ToggleButton value="project" className={styles.toggleButton} disabled={disabled}>
              <span className={styles.toggleButtonLabel}>Proj.</span>
              <Icon path={mdiFolderStar} size={1.25} />
            </ToggleButton>,
            // <ToggleButton value="meeting" className={styles.toggleButton} disabled={disabled}>
            //   <span className={styles.toggleButtonLabel}>Mtg.</span>
            //   <Icon path={mdiGoogleClassroom} size={1.25} />
            // </ToggleButton>,
          ]}
          <ToggleButton value="successCriteria" className={styles.toggleButton} disabled={disabled}>
            <span className={styles.toggleButtonLabel}>Suc. Crit.</span>
            <Icon path={mdiRhombusSplit} size={1.25} />
          </ToggleButton>
          <ToggleButton value="rock" className={styles.toggleButton} disabled={disabled}>
            <span className={styles.toggleButtonLabel}>Rock</span>
            <Icon path={mdiRhombus} size={1.25} />
          </ToggleButton>
          <ToggleButton value="objective" className={styles.toggleButton} disabled={disabled}>
            <span className={styles.toggleButtonLabel}>Obj.</span>
            <Icon path={mdiChartTimelineVariant} size={1.25} />
          </ToggleButton>
          <ToggleButton value="metric" className={styles.toggleButton} disabled={disabled}>
            <span className={styles.toggleButtonLabel}>Metric</span>
            <Icon path={mdiPoll} size={1.25} />
          </ToggleButton>
          <ToggleButton value="weeklyTarget" className={styles.toggleButton} disabled={disabled}>
            <span className={styles.toggleButtonLabel}>KPI</span>
            <Icon path={mdiBullseyeArrow} size={1.25} />
          </ToggleButton>
          <ToggleButton value="issue" className={styles.toggleButton} disabled={disabled}>
            <span className={styles.toggleButtonLabel}>Issue</span>
            <Icon path={mdiAlertDecagram} size={1.25} />
          </ToggleButton>
          <ToggleButton value="todo" className={styles.toggleButton} disabled={disabled}>
            <span className={styles.toggleButtonLabel}>To do</span>
            <Icon path={mdiCheckboxMarked} size={1.25} />
          </ToggleButton>
        </ToggleButtonGroup>
        {Boolean(_.get(touched, refTypeName) && _.get(errors, refTypeName)) && (
          <Typography
            variant="caption"
            style={{
              display: "block",
              color: theme.palette.error.main,
              textAlign: "center",
              marginTop: theme.spacing(0.5),
            }}
          >
            {_.get(errors, refTypeName)}
          </Typography>
        )}
      </div>
      {refType && (
        <div style={{ marginTop: theme.spacing(2) }}>
          {useRefIds ? (
            renderMultiSelectRefs(refType)
          ) : useRefContext ? (
            renderReferenceContext(refType)
          ) : (
            <Autocomplete
              disabled={disabled}
              options={menuItems}
              value={selectedItem || ""}
              inputValue={inputSearchTerm}
              getOptionLabel={(option) => {
                let value = _.get(option, "value") || "";

                if (isPlanReference) {
                  value = _.get(option, "departmentName") || "";
                  const category = _.get(option, "category");
                  const categoryStr = _.get(PLAN_CATEGORIES, category);

                  if (categoryStr) value += ` (${categoryStr})`;
                }

                return value;
              }}
              renderOption={(option, state) => {
                if (isPlanReference) {
                  return renderReferenceItem(option);
                }

                const { id } = option;
                const plans = getPlansByReferenceModel(refType, option);
                const uniqPlans = _.uniqBy(plans, (plan) => plan.sharedPlanId);
                return (
                  <div value={id} className={styles.tieInItem}>
                    {uniqPlans.map((plan, idx) => (
                      <PlanPill plan={plan} key={idx} />
                    ))}
                    {_.isEmpty(uniqPlans) && <PlanPill plan={null} />}
                    {renderReferenceItem(option)}
                  </div>
                );
              }}
              ListboxProps={{
                onScroll: (e) => loadMoreItems(e, refType),
              }}
              fullWidth
              onChange={handleChangeRefId}
              onInputChange={(e, newInputValue) => setInputSearchTerm(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={refIdLabel(refType)}
                  error={Boolean(_.get(touched, refIdName) && _.get(errors, refIdName))}
                  helperText={_.get(touched, refIdName) && _.get(errors, refIdName)}
                />
              )}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ReferenceSelector;

const GET_NON_TODO_DATA = gql`
  query AddTodoDialog_GetScRocksObjectivesMetrics($organization: ID!, $sharedPlanId: ID, $corpPlan: ID, $currQuarter: Boolean) {
    successCriterias(organization: $organization, oneYearCorpPlan: $corpPlan, currQuarter: $currQuarter, sharedPlanId: $sharedPlanId) {
      id
      value
      orderIdx
      rock {
        id
        value
        orgNumber
        plan {
          id
          departmentName
          color
          shortName
          sharedPlanId
        }
      }
    }

    rocks(organization: $organization, sharedPlanId: $sharedPlanId, oneYearCorpPlan: $corpPlan, currQuarter: $currQuarter) {
      id
      value
      index
      orgNumber
      objective {
        id
        value
        number
      }
      plan {
        id
        shortName
        color
        departmentName
        sharedPlanId
      }
    }

    objectives(organization: $organization, sharedPlanId: $sharedPlanId, corpPlan: $corpPlan, includeThreeYear: true) {
      id
      value
      category
      number
      plan {
        id
        year
        shortName
        color
        departmentName
        sharedPlanId
        useDepartmentTheme
        theme
      }
    }

    metrics(organization: $organization, sharedPlanId: $sharedPlanId, corpPlan: $corpPlan, includeThreeYear: true) {
      id
      value
      category
      number
      plan {
        id
        year
        shortName
        color
        departmentName
        sharedPlanId
        useDepartmentTheme
        theme
      }
    }

    weeklyTargets(organization: $organization, sharedPlanId: $sharedPlanId, oneYearCorpPlan: $corpPlan) {
      id: _id
      value
      plans {
        year
        shortName
        color
        departmentName
        sharedPlanId
      }
    }

    threeYearPlans: plans(organization: $organization, category: "3 year") {
      id
      shortName
      color
      departmentName
      sharedPlanId
      category
    }

    oneYearPlans: plans(organization: $organization, oneYearCorpPlan: $corpPlan) {
      id
      shortName
      color
      departmentName
      sharedPlanId
      category
    }

    projects(organization: $organization, sharedPlanId: $sharedPlanId) {
      id
      value
      plan {
        shortName
        color
        departmentName
        sharedPlanId
      }
    }

    # meetings(organization: $organization) {
    #   id
    #   title
    #   plan {
    #     shortName
    #     color
    #     departmentName
    #     sharedPlanId
    #   }
    # }
  }
`;

const GET_REFERENCED_TODO_ISSUE = gql`
  query AddTodoDialog_GetRefTodoCreate($id: ID) {
    todo(id: $id) {
      id: _id
      value
      plan {
        id
        shortName
        color
        departmentName
        sharedPlanId
      }
    }
  }
`;

const GET_MORE_TODOS = gql`
  query AddTodoDialog_GetMoreTodos($organization: ID!, $sharedPlanId: ID, $cursor: Int, $limit: Int, $oneYearCorpPlan: ID) {
    results: moreTodos(
      organization: $organization
      category: "todo"
      cursor: $cursor
      limit: $limit
      sharedPlanId: $sharedPlanId
      oneYearCorpPlan: $oneYearCorpPlan
    ) {
      todos {
        id: _id
        value
        orgNumber
        description
        plan {
          id
          shortName
          color
          departmentName
          sharedPlanId
        }
      }
      nextCursor
    }
  }
`;

const GET_MORE_ISSUES = gql`
  query AddTodoDialog_GetMoreIssues($organization: ID!, $sharedPlanId: ID, $cursor: Int, $limit: Int, $oneYearCorpPlan: ID) {
    results: moreTodos(
      organization: $organization
      category: "issue"
      cursor: $cursor
      limit: $limit
      sharedPlanId: $sharedPlanId
      oneYearCorpPlan: $oneYearCorpPlan
    ) {
      todos {
        id: _id
        value
        orgNumber
        description
        plan {
          id
          shortName
          color
          departmentName
          sharedPlanId
        }
      }
      nextCursor
    }
  }
`;
